import { useState, useEffect } from 'react';
import { X, Download, ChevronLeft, ChevronRight, CheckCircle2, RotateCcw, Award, BarChart3, List, TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { normalizeLanguage } from '../i18n';

interface GrowthRecordsModalProps {
  isOpen: boolean;
  onClose: () => void;
  classId: number;
}

interface PointHistory {
  id: number;
  studentId: number;
  amount: number;
  reason: string;
  createdAt: string;
  revertedFromId?: number | null;
  revertedAt?: string | null;
  student: {
    id: number;
    name: string;
  };
}

interface Meta {
  totalItems: number;
  itemCount: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
}

interface StatsData {
  period: string;
  startDate: string;
  endDate: string;
  summary: {
    totalGained: number;
    totalLost: number;
    netChange: number;
    totalActions: number;
    activeStudents: number;
  };
  studentStats: {
    studentId: number;
    studentName: string;
    totalGained: number;
    totalLost: number;
    netChange: number;
    actionCount: number;
  }[];
  dailyStats: {
    date: string;
    gained: number;
    lost: number;
    count: number;
  }[];
}

export default function GrowthRecordsModal({ isOpen, onClose, classId }: GrowthRecordsModalProps) {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<'stats' | 'history'>('stats');
  const [history, setHistory] = useState<PointHistory[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [undoTarget, setUndoTarget] = useState<PointHistory | null>(null);
  const [undoSubmitting, setUndoSubmitting] = useState(false);
  const activeLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language);

  // Stats state
  const [statsPeriod, setStatsPeriod] = useState<'week' | 'month' | 'lastMonth' | 'custom'>('week');
  const [stats, setStats] = useState<StatsData | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  const fetchHistory = async (pageToFetch: number) => {
    setLoading(true);
    try {
      const response = await api.get(`/students/class/${classId}/history`, {
        params: { page: pageToFetch, limit: 20 }
      });
      setHistory(response.data.items);
      setMeta(response.data.meta);
      setPage(pageToFetch);
    } catch (error) {
      console.error('Failed to fetch history', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (period: 'week' | 'month' | 'lastMonth' | 'custom', startDate?: string, endDate?: string) => {
    setStatsLoading(true);
    try {
      const params: Record<string, string> = { period };
      if (period === 'custom' && startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      const response = await api.get(`/students/class/${classId}/stats`, { params });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats', error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && classId) {
      if (activeTab === 'history') {
        fetchHistory(1);
      } else {
        fetchStats(statsPeriod);
      }
    }
  }, [isOpen, classId, activeTab]);

  useEffect(() => {
    if (isOpen && classId && activeTab === 'stats' && statsPeriod !== 'custom') {
      fetchStats(statsPeriod);
    }
  }, [statsPeriod]);

  const submitUndo = async () => {
    if (!undoTarget) return;
    setUndoSubmitting(true);
    try {
      await api.post(`/students/history/${undoTarget.id}/revert`);
      setUndoTarget(null);
      await fetchHistory(1);
    } catch (error) {
      console.error('Undo failed', error);
      alert(t('growthRecords.undoFailed'));
    } finally {
      setUndoSubmitting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/students/class/${classId}/history`, {
        params: { page: 1, limit: 10000 }
      });
      const items = response.data.items as PointHistory[];

      const headers = [
        t('growthRecords.csv.time'),
        t('growthRecords.csv.student'),
        t('growthRecords.csv.action'),
        t('growthRecords.csv.change'),
      ];
      const csvContent = [
        headers.join(','),
        ...items.map(item => {
          const date = new Date(item.createdAt).toLocaleString(activeLanguage);
          return [
            `"${date}"`,
            `"${item.student?.name || t('growthRecords.unknownStudent')}"`,
            `"${item.amount === 0 ? t('common.prestigeReason') : (item.reason || '')}"`,
            `"${item.amount > 0 ? '+' + item.amount : item.amount}"`
          ].join(',');
        })
      ].join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const now = new Date();
      const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      link.setAttribute('download', t('growthRecords.exportFilename', { dateStr }));

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export failed', error);
      alert(t('growthRecords.exportFailed'));
    }
  };

  const handleExportStats = () => {
    if (!stats) return;
    const periodLabelMap: Record<string, string> = {
      week: t('growthRecords.stats.week'),
      month: t('growthRecords.stats.month'),
      lastMonth: t('growthRecords.stats.lastMonth'),
      custom: t('growthRecords.stats.custom'),
    };
    const periodLabel = periodLabelMap[statsPeriod] || statsPeriod;

    const lines: string[] = [];

    // Summary section
    lines.push(`${periodLabel} ${t('growthRecords.stats.period', { start: stats.startDate, end: stats.endDate })}`);
    lines.push('');
    lines.push([
      t('growthRecords.stats.totalGained'),
      t('growthRecords.stats.totalLost'),
      t('growthRecords.stats.netChange'),
      t('growthRecords.stats.totalActions'),
      t('growthRecords.stats.activeStudents'),
    ].join(','));
    lines.push([
      stats.summary.totalGained,
      stats.summary.totalLost,
      stats.summary.netChange,
      stats.summary.totalActions,
      stats.summary.activeStudents,
    ].join(','));

    // Student ranking section
    lines.push('');
    lines.push(t('growthRecords.stats.studentRanking'));
    lines.push([
      t('growthRecords.stats.rank'),
      t('growthRecords.stats.student'),
      t('growthRecords.stats.gained'),
      t('growthRecords.stats.lost'),
      t('growthRecords.stats.net'),
      t('growthRecords.stats.actions'),
    ].join(','));
    stats.studentStats.forEach((s, idx) => {
      lines.push([idx + 1, `"${s.studentName}"`, s.totalGained, s.totalLost, s.netChange, s.actionCount].join(','));
    });

    // Daily trend section
    if (stats.dailyStats.length > 0) {
      lines.push('');
      lines.push(t('growthRecords.stats.dailyTrend'));
      lines.push([
        t('growthRecords.csv.time'),
        t('growthRecords.stats.gained'),
        t('growthRecords.stats.lost'),
        t('growthRecords.stats.actions'),
      ].join(','));
      stats.dailyStats.forEach(d => {
        lines.push([d.date, d.gained, d.lost, d.count].join(','));
      });
    }

    const csvContent = lines.join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    link.setAttribute('download', t('growthRecords.stats.exportFilename', { period: periodLabel, dateStr }));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  // Group history by date
  const groupedHistory = history.reduce((groups, item) => {
    const dateObj = new Date(item.createdAt);
    const date = `${dateObj.getFullYear()}/${dateObj.getMonth() + 1}/${dateObj.getDate()}`;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(item);
    return groups;
  }, {} as Record<string, PointHistory[]>);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-3xl rounded-3xl shadow-2xl flex flex-col h-[85vh] animate-in zoom-in-95 duration-300 overflow-hidden relative">
        {/* Header */}
        <div className="p-6 pb-3 border-b border-gray-100 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-500">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">{t('growthRecords.title')}</h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex px-6 pt-3 gap-1 bg-white">
          <button
            onClick={() => setActiveTab('stats')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-colors ${
              activeTab === 'stats'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            {t('growthRecords.tabs.stats')}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-colors ${
              activeTab === 'history'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
            }`}
          >
            <List className="w-4 h-4" />
            {t('growthRecords.tabs.history')}
            {meta && <span className="text-xs opacity-70 ml-1">({meta.totalItems})</span>}
          </button>
        </div>

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <>
            {/* Period Toggle */}
            <div className="px-6 pt-4 pb-2 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                {(['week', 'month', 'lastMonth'] as const).map((p) => (
                  <button
                    key={p}
                    onClick={() => { setStatsPeriod(p); setShowCustomPicker(false); }}
                    className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
                      statsPeriod === p
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {t(`growthRecords.stats.${p}`)}
                  </button>
                ))}
                <button
                  onClick={() => {
                    setStatsPeriod('custom');
                    setShowCustomPicker(true);
                    if (!customStartDate) {
                      const now = new Date();
                      const pad = (n: number) => String(n).padStart(2, '0');
                      const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
                      const weekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
                      const weekAgoStr = `${weekAgo.getFullYear()}-${pad(weekAgo.getMonth() + 1)}-${pad(weekAgo.getDate())}`;
                      setCustomStartDate(weekAgoStr);
                      setCustomEndDate(todayStr);
                    }
                  }}
                  className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors flex items-center gap-1 ${
                    statsPeriod === 'custom'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  <Calendar className="w-3.5 h-3.5" />
                  {t('growthRecords.stats.custom')}
                </button>
                {stats && (
                  <>
                    <span className="text-xs text-gray-400 ml-auto">
                      {t('growthRecords.stats.period', { start: stats.startDate, end: stats.endDate })}
                    </span>
                    <button
                      onClick={handleExportStats}
                      className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
                      title={t('growthRecords.exportCsv')}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              {showCustomPicker && statsPeriod === 'custom' && (
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                  <span className="text-gray-400 text-sm">~</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                  <button
                    onClick={() => {
                      if (customStartDate && customEndDate) {
                        fetchStats('custom', customStartDate, customEndDate);
                      }
                    }}
                    disabled={!customStartDate || !customEndDate}
                    className="px-4 py-1.5 bg-blue-500 text-white rounded-lg text-sm font-bold hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('growthRecords.stats.query')}
                  </button>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-6 pt-2 space-y-5">
              {statsLoading ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                </div>
              ) : !stats || stats.summary.totalActions === 0 ? (
                <div className="text-center py-10 text-gray-400">{t('growthRecords.stats.noData')}</div>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-green-50 rounded-2xl p-4 text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <TrendingUp className="w-4 h-4 text-green-500" />
                        <span className="text-xs font-bold text-green-600">{t('growthRecords.stats.totalGained')}</span>
                      </div>
                      <div className="text-2xl font-black text-green-600">+{stats.summary.totalGained}</div>
                    </div>
                    <div className="bg-red-50 rounded-2xl p-4 text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <TrendingDown className="w-4 h-4 text-red-400" />
                        <span className="text-xs font-bold text-red-400">{t('growthRecords.stats.totalLost')}</span>
                      </div>
                      <div className="text-2xl font-black text-red-400">{stats.summary.totalLost}</div>
                    </div>
                    <div className="bg-blue-50 rounded-2xl p-4 text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <BarChart3 className="w-4 h-4 text-blue-500" />
                        <span className="text-xs font-bold text-blue-500">{t('growthRecords.stats.netChange')}</span>
                      </div>
                      <div className={`text-2xl font-black ${stats.summary.netChange >= 0 ? 'text-blue-600' : 'text-red-400'}`}>
                        {stats.summary.netChange >= 0 ? '+' : ''}{stats.summary.netChange}
                      </div>
                    </div>
                  </div>

                  {/* Sub stats */}
                  <div className="flex gap-4 justify-center text-sm">
                    <span className="text-gray-400">
                      {t('growthRecords.stats.totalActions')}: <span className="font-bold text-gray-600">{stats.summary.totalActions}</span>
                    </span>
                    <span className="text-gray-400">
                      {t('growthRecords.stats.activeStudents')}: <span className="font-bold text-gray-600">{stats.summary.activeStudents}</span>
                    </span>
                  </div>

                  {/* Daily Trend - Line Chart */}
                  {stats.dailyStats.length > 0 && (() => {
                    const chartW = 500;
                    const chartH = 160;
                    const padL = 40;
                    const padR = 16;
                    const padT = 16;
                    const padB = 36;
                    const innerW = chartW - padL - padR;
                    const innerH = chartH - padT - padB;
                    const days = stats.dailyStats;
                    const allVals = days.flatMap(d => [d.gained, Math.abs(d.lost)]);
                    const maxVal = Math.max(...allVals, 1);
                    const stepX = days.length > 1 ? innerW / (days.length - 1) : 0;
                    const yScale = (v: number) => padT + innerH - (v / maxVal) * innerH;
                    const xPos = (i: number) => padL + (days.length > 1 ? i * stepX : innerW / 2);

                    const gainedPoints = days.map((d, i) => `${xPos(i)},${yScale(d.gained)}`).join(' ');
                    const lostPoints = days.map((d, i) => `${xPos(i)},${yScale(Math.abs(d.lost))}`).join(' ');

                    // Y-axis grid lines
                    const gridCount = 4;
                    const gridLines = Array.from({ length: gridCount + 1 }, (_, i) => {
                      const val = Math.round((maxVal / gridCount) * i);
                      const y = yScale(val);
                      return { val, y };
                    });

                    return (
                      <div>
                        <h3 className="text-sm font-bold text-gray-500 mb-3">{t('growthRecords.stats.dailyTrend')}</h3>
                        <div className="bg-gray-50 rounded-2xl p-3 overflow-x-auto">
                          <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full" style={{ minWidth: days.length > 7 ? `${days.length * 56}px` : undefined }}>
                            {/* Grid lines */}
                            {gridLines.map(g => (
                              <g key={g.val}>
                                <line x1={padL} y1={g.y} x2={chartW - padR} y2={g.y} stroke="#e5e7eb" strokeWidth="1" />
                                <text x={padL - 6} y={g.y + 3} textAnchor="end" className="fill-gray-400" fontSize="10">{g.val}</text>
                              </g>
                            ))}

                            {/* Gained line */}
                            <polyline points={gainedPoints} fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                            {days.map((d, i) => {
                              const ds = typeof d.date === 'string' ? d.date.slice(0, 10) : new Date(d.date).toISOString().slice(0, 10);
                              const tip = `${ds}\n${t('growthRecords.stats.gained')}: +${d.gained}\n${t('growthRecords.stats.lost')}: ${d.lost}\n${t('growthRecords.stats.actions')}: ${d.count}`;
                              return (
                                <g key={`g-${i}`} className="cursor-pointer">
                                  <circle cx={xPos(i)} cy={yScale(d.gained)} r="12" fill="transparent" />
                                  <circle cx={xPos(i)} cy={yScale(d.gained)} r="4" fill="#4ade80" stroke="white" strokeWidth="2" className="transition-all hover:r-[6]" />
                                  <title>{tip}</title>
                                </g>
                              );
                            })}

                            {/* Lost line */}
                            <polyline points={lostPoints} fill="none" stroke="#f87171" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                            {days.map((d, i) => {
                              const ds = typeof d.date === 'string' ? d.date.slice(0, 10) : new Date(d.date).toISOString().slice(0, 10);
                              const tip = `${ds}\n${t('growthRecords.stats.gained')}: +${d.gained}\n${t('growthRecords.stats.lost')}: ${d.lost}\n${t('growthRecords.stats.actions')}: ${d.count}`;
                              return (
                                <g key={`l-${i}`} className="cursor-pointer">
                                  <circle cx={xPos(i)} cy={yScale(Math.abs(d.lost))} r="12" fill="transparent" />
                                  <circle cx={xPos(i)} cy={yScale(Math.abs(d.lost))} r="4" fill="#f87171" stroke="white" strokeWidth="2" />
                                  <title>{tip}</title>
                                </g>
                              );
                            })}

                            {/* Hover columns — full-height invisible touch targets */}
                            {days.map((d, i) => {
                              const ds = typeof d.date === 'string' ? d.date.slice(0, 10) : new Date(d.date).toISOString().slice(0, 10);
                              const tip = `${ds}\n${t('growthRecords.stats.gained')}: +${d.gained}  ${t('growthRecords.stats.lost')}: ${d.lost}\n${t('growthRecords.stats.actions')}: ${d.count}`;
                              const colW = days.length > 1 ? stepX : innerW;
                              return (
                                <rect key={`hover-${i}`} x={xPos(i) - colW / 2} y={padT} width={colW} height={innerH} fill="transparent" className="cursor-pointer">
                                  <title>{tip}</title>
                                </rect>
                              );
                            })}

                            {/* X-axis labels */}
                            {days.map((day, i) => {
                              const ds = typeof day.date === 'string' ? day.date.slice(0, 10) : new Date(day.date).toISOString().slice(0, 10);
                              const [, mm, dd] = ds.split('-');
                              return (
                                <text key={`x-${i}`} x={xPos(i)} y={chartH - 6} textAnchor="middle" className="fill-gray-400" fontSize="10">
                                  {`${Number(mm)}/${Number(dd)}`}
                                </text>
                              );
                            })}

                            {/* Value labels on points */}
                            {days.map((d, i) => (
                              <g key={`v-${i}`} className="pointer-events-none">
                                {d.gained > 0 && (
                                  <text x={xPos(i)} y={yScale(d.gained) - 8} textAnchor="middle" className="fill-green-600" fontSize="9" fontWeight="bold">+{d.gained}</text>
                                )}
                                {d.lost < 0 && (
                                  <text x={xPos(i)} y={yScale(Math.abs(d.lost)) - 8} textAnchor="middle" className="fill-red-400" fontSize="9" fontWeight="bold">{d.lost}</text>
                                )}
                              </g>
                            ))}
                          </svg>
                          {/* Legend */}
                          <div className="flex items-center justify-center gap-4 mt-2">
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-0.5 bg-green-400 rounded"></div>
                              <span className="text-xs text-gray-400">{t('growthRecords.stats.gained')}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-0.5 bg-red-400 rounded"></div>
                              <span className="text-xs text-gray-400">{t('growthRecords.stats.lost')}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Student Ranking Table */}
                  {stats.studentStats.length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-gray-500 mb-3">{t('growthRecords.stats.studentRanking')}</h3>
                      <div className="border border-gray-100 rounded-2xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 text-gray-400 text-xs">
                              <th className="py-2 px-3 text-left font-bold">{t('growthRecords.stats.rank')}</th>
                              <th className="py-2 px-3 text-left font-bold">{t('growthRecords.stats.student')}</th>
                              <th className="py-2 px-3 text-right font-bold">{t('growthRecords.stats.gained')}</th>
                              <th className="py-2 px-3 text-right font-bold">{t('growthRecords.stats.lost')}</th>
                              <th className="py-2 px-3 text-right font-bold">{t('growthRecords.stats.net')}</th>
                              <th className="py-2 px-3 text-right font-bold">{t('growthRecords.stats.actions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stats.studentStats.map((s, idx) => (
                              <tr key={s.studentId} className="border-t border-gray-50 hover:bg-gray-50/50">
                                <td className="py-2 px-3 text-gray-400 font-bold">{idx + 1}</td>
                                <td className="py-2 px-3 font-bold text-gray-800">{s.studentName}</td>
                                <td className="py-2 px-3 text-right text-green-600 font-bold">+{s.totalGained}</td>
                                <td className="py-2 px-3 text-right text-red-400 font-bold">{s.totalLost}</td>
                                <td className={`py-2 px-3 text-right font-black ${s.netChange >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
                                  {s.netChange >= 0 ? '+' : ''}{s.netChange}
                                </td>
                                <td className="py-2 px-3 text-right text-gray-400">{s.actionCount}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <>
            {/* Actions */}
            <div className="px-6 py-3 flex gap-3 bg-gray-50/50 items-center">
              <button
                onClick={handleExport}
                className="py-2 px-4 bg-indigo-50 text-indigo-600 font-bold rounded-xl hover:bg-indigo-100 transition-colors flex items-center gap-2 text-sm"
              >
                <Download className="w-4 h-4" />
                {t('growthRecords.exportCsv')}
              </button>
              {meta && (
                <span className="text-gray-400 text-sm ml-auto">
                  {t('growthRecords.count', { shown: meta.itemCount, total: meta.totalItems })}
                </span>
              )}
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {loading ? (
                 <div className="flex justify-center py-10">
                   <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                 </div>
              ) : Object.keys(groupedHistory).length === 0 ? (
                 <div className="text-center py-10 text-gray-400">{t('growthRecords.empty')}</div>
              ) : (
                Object.entries(groupedHistory).map(([date, items]) => (
                  <div key={date}>
                    <div className="flex items-center gap-4 mb-4">
                       <div className="h-px bg-gray-100 flex-1"></div>
                       <span className="text-xs font-bold text-gray-400 bg-gray-50 px-3 py-1 rounded-full">{date}</span>
                       <div className="h-px bg-gray-100 flex-1"></div>
                    </div>
                    <div className="space-y-3">
                      {items.map(item => {
                        const timeStr = new Date(item.createdAt).toLocaleTimeString(activeLanguage, { hour: '2-digit', minute: '2-digit', hour12: false });
                        const isPrestige = item.amount === 0;
                        const isUndoRecord = item.revertedFromId != null;
                        const isReverted = item.revertedAt != null;
                        const canUndo = !isPrestige && !isUndoRecord && !isReverted && item.amount !== 0;

                        return (
                          <div key={item.id} className={`bg-white border border-gray-100 rounded-2xl p-4 flex items-center gap-4 shadow-sm relative ${isReverted ? 'opacity-60' : ''}`}>
                            <span className="text-gray-400 font-mono text-sm">
                              {timeStr}
                            </span>

                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                              isPrestige
                                ? 'bg-yellow-100 text-yellow-500'
                                : isUndoRecord
                                  ? 'bg-indigo-100 text-indigo-500'
                                  : item.amount >= 0 ? 'bg-green-100 text-green-500' : 'bg-gray-100 text-gray-500'
                            }`}>
                               {isPrestige ? <Award className="w-4 h-4" /> : (isUndoRecord ? <RotateCcw className="w-4 h-4" /> : (item.amount >= 0 ? <CheckCircle2 className="w-4 h-4" /> : <RotateCcw className="w-4 h-4" />))}
                            </div>

                            <div className="flex-1">
                              <span className="font-bold text-gray-800 mr-2">{item.student?.name}</span>
                              <span className={`${isPrestige ? 'text-yellow-600 font-bold' : 'text-gray-500'}`}>
                                {isPrestige ? t('common.prestigeReason') : item.reason}
                                {isReverted && <span className="ml-2 text-xs font-bold text-gray-400">{t('growthRecords.reverted')}</span>}
                              </span>
                            </div>

                            {!isPrestige && (
                              <div className="flex items-center gap-3">
                                <div className={`font-black text-lg ${item.amount >= 0 ? 'text-green-500' : 'text-gray-400'}`}>
                                  {item.amount > 0 ? '+' + item.amount : item.amount}
                                  <span className="text-base ml-1">🐟</span>
                                </div>
                                {canUndo && (
                                  <button
                                    onClick={() => setUndoTarget(item)}
                                    className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-bold hover:bg-gray-200 transition-colors"
                                  >
                                    {t('growthRecords.undo')}
                                  </button>
                                )}
                              </div>
                            )}

                            {isReverted && (
                              <div className="pointer-events-none absolute left-4 right-4 top-1/2 -translate-y-1/2 border-t border-gray-300"></div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>

            {undoTarget && (
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6">
                <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-5">
                  <div className="text-lg font-black text-gray-800">{t('growthRecords.confirmUndo.title')}</div>
                  <div className="mt-2 text-sm text-gray-500 leading-relaxed">
                    {t('growthRecords.confirmUndo.desc', {
                      name: undoTarget.student?.name || t('growthRecords.confirmUndo.defaultStudent'),
                      action: undoTarget.amount > 0 ? t('growthRecords.confirmUndo.add') : t('growthRecords.confirmUndo.deduct'),
                    })}
                  </div>
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={() => setUndoTarget(null)}
                      disabled={undoSubmitting}
                      className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-600 font-bold hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={submitUndo}
                      disabled={undoSubmitting}
                      className="flex-1 py-2.5 rounded-xl bg-red-50 text-red-600 font-bold hover:bg-red-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {undoSubmitting ? t('common.loading') : t('growthRecords.confirmUndo.confirm')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Pagination */}
            {meta && meta.totalPages > 1 && (
              <div className="p-4 border-t border-gray-100 bg-white flex items-center justify-between">
                <span className="text-sm text-gray-500 font-medium">
                  {t('growthRecords.pagination', {
                    current: meta.currentPage,
                    totalPages: meta.totalPages,
                    totalItems: meta.totalItems,
                  })}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchHistory(page - 1)}
                    disabled={page <= 1}
                    className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => fetchHistory(page + 1)}
                    disabled={page >= meta.totalPages}
                    className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
